import type { TileableModel } from '@rocco/ui/tile-shared/view-models/tile';
import { isProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { isProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { isProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';

import type { BulkEnquiryItem } from './bulk-enquiry-state';

/**
 * Build the bulk-enquiry payload for a directory tile model, or null when the
 * model isn't an enquirable entity (e.g. an article). The item's SiteTree id is
 * the model id; the target professional is the seller/owner — or, for a
 * professional tile, the professional itself.
 */
export const bulkEnquiryItemForModel = (
    model: TileableModel,
): BulkEnquiryItem | null => {
    if (isProfessionalTileViewModel(model)) {
        // A professional reads far better as their logo than as whichever hero
        // shot fronts their profile — fall back to the hero only if they have no
        // logo.
        const logo = model.logo?.image?.src;
        return {
            siteTreeID: model.id,
            targetProfessionalId: model.id,
            itemType: 'professional',
            title: model.title,
            image: logo ?? model.image?.src,
            imageIsLogo: Boolean(logo),
            imageBackground: logo ? model.logo?.background : undefined,
            // A professional tile is its own supplier.
            supplierName: model.title,
            category: model.category,
        };
    }

    if (isProductTileViewModel(model)) {
        return {
            siteTreeID: model.id,
            targetProfessionalId: model.professional.id,
            itemType: 'product',
            title: model.title,
            image: model.images?.[0]?.src,
            supplierName: model.professional?.name,
        };
    }

    if (isProjectTileViewModel(model)) {
        return {
            siteTreeID: model.id,
            targetProfessionalId: model.professional.id,
            itemType: 'project',
            title: model.title,
            image: model.images?.[0]?.src,
            supplierName: model.professional?.name,
        };
    }

    return null;
};
